<template>
  <modal :state="state" class="formula-modal md" @toggle-modal="cancel" :should-cancel-on-outside-click="false" prevent-esc>
    <div slot="body" ref="formulaModal">
      <h3 class="modal-title">{{targetFormula.originalText ? 'Edit' : 'Add'}} Formula</h3>
      <p>In the space below, you can write functions from scratch, insert from a preselected list, or add model properties to create a new formula.</p>

      <formula-editor
        ref="formulaEditor"
        v-if="state.isVisible"
        id="formula-field"
        v-model="targetFormula.text"
        :codeMirrorAble="formula"
        :dynamicOptions="dynamicOptions"
        :config="{autoClearEmptyLines: false, lineWrapping: true, lineNumbers: true}"
        @openFieldsPopupMenu="openPopupOnClick($event)"
        @chip-cleared="resetMark">
      </formula-editor>

      <div class="cta mt-3">
        <button type="button" class="btn grey block text-nowrap max-width-fit-content" @click="openPopup($event, 'formula')">
          Insert Formula Functions
        </button>
        <button type="button" v-if="hasDynamicOptions" class="btn grey block text-nowrap max-width-fit-content" @click="openPopup($event, 'fields')">
          Insert {{getDynamicFieldsTitle(dynamicOptions[0])}}
        </button>
      </div>

      <context-menu
        ref="formulaContextMenu"
        :state="modalStateFor(vueModalID.formulaFunctions)"
        :index="vueModalID.formulaFunctions"
        fixed
        @close="closePopup($event, 'formula')">
        <dynamic-fields-tree
          v-bind="formulaDynamicFieldsProps"
          @select="addWidget(WidgetType.FormulaFunctionWidget, $event)">
          <template #heading>
            <h4 class="popup-title">Formula Functions</h4>
          </template>
        </dynamic-fields-tree>
      </context-menu>

      <context-menu
        v-if="hasDynamicOptions"
        ref="fieldsContextMenu"
        :state="modalStateFor(vueModalID.formFields)"
        :index="vueModalID.formFields"
        fixed
        @close="closePopup($event, 'fields')">
        <component
          v-for="options in dynamicOptions"
          :key="`dynamic-option-${options.title}-${index}`"
          :is="options.is"
          @select="addWidget(WidgetType.FormFieldWidget, $event)"
          v-bind="options.props">
          <template #heading>
            <h4 class="popup-title">{{getDynamicFieldsTitle(options)}}</h4>
          </template>
        </component>
      </context-menu>

      <div class="rule-preview">
        <h5>Preview</h5>

        <div class="condition-block">
          <p>{{formulaEvaluation}}</p>
          <p v-if="currentParseError">{{currentParseError}}</p>
        </div>
      </div>

      <p class="text-left">
        <a href="/formulas" class="link" target="_blank">Test this formula</a><help-tooltip text="This formula is calculated on the server. Please test the formula prior to publishing."></help-tooltip>
      </p>

      <flash-attribute-messages v-if="shouldValidate && !targetFormula.isComplete()" messages="You must complete the formula before you can continue."></flash-attribute-messages>

      <div class="cta">
        <button v-if="targetFormula.originalText" class="btn block red outline" @click.prevent="remove($event)">Remove</button>
        <button class="btn block red outline" @click.prevent="cancel">Cancel</button>
        <button class="btn block green" @click.prevent="save">Save</button>
      </div>
    </div>
  </modal>
</template>

<script lang="ts">
  import Vue from "vue";
  import ModalComponentMixin from "../../mixins/modalComponentMixin";
  import DynamicFieldsTree from "../../../common/components/dynamicFieldsTrees/dynamicFieldsTree.vue";
  import DeterministicDynamicFieldsTree from "~/components/dynamicFieldsTrees/deterministic-dynamic-fields-tree.vue";
  import Form from "../../../formability/form_builder/models/form";
  import {ComponentContext} from "../../enums/componentContext";
  import ContextMenu from "../contextMenu.vue";
  import Formula from "../../models/formula";
  import cTooltip from "../../modules/cTooltip/tooltipManager";
  import "codemirror/lib/codemirror.css";
  import "codemirror/addon/search/searchcursor";
  import {WidgetType} from "../../helpers/codeMirrorWidget";
  import ObjectModel from "../../models/objectModel";
  import {IDynamicFieldsOptions} from "../../interfaces/dynamicFieldsInterfaces/dynamicOptionsInterfaces";
  import FormulaFunctionsDynamicFieldsHandler from "../../models/dynamicFields/handlers/formulaFunctionsDynamicFieldsHandler";
  import DynamicFieldMixin from "../../../common/mixins/dynamicFieldMixin";
  import FormulaEditor from "./formula-editor.vue"
  import {Hash} from "../../interfaces/hashInterfaces";
  import CodeMirrorEditor from "../../helpers/codeMirrorEditor";
  import FlashAttributeMessages from "../flash/flash-attribute-messages.vue";
  import { IDynamicDataConfig } from '~/interfaces/dynamicFieldsInterfaces'
  import { VariableLookupFunction } from '~/formulaContext'
  import {ParseError, parseFormula} from "formula-engine";

  export default Vue.extend({
    components: {
      DeterministicDynamicFieldsTree,
      DynamicFieldsTree,
      ContextMenu,
      FormulaEditor,
      FlashAttributeMessages
    },
    mixins: [
      ModalComponentMixin,
      DynamicFieldMixin,
    ],
    props: {
      parent: {
        type: Object
      },
      forms: {
        type: Array as () => Array<Form>,
        default: () => []
      },
      objectModels: {
        type: Array as () => Array<ObjectModel>,
        default: () => []
      },
      formula: {
        type: Object as () => Formula
      },
      dynamicOptions: {
        type: Array as () => Array<IDynamicFieldsOptions>,
        default: () => []
      },
    },
    data: function(): (Hash & {targetFormula: Formula}){
      return {
        ComponentContext,
        editMode: false,
        shouldValidate: false,
        cTooltip: new cTooltip(),
        contextMenu: {
          formula: {
            id: '',
            ref: 'formulaContextMenu'
          },
          fields: {
            id: '',
            ref: 'fieldsContextMenu'
          },
        },
        markToRemove: null,
        isReplacingPlaceholder: false,
        WidgetType,
        targetFormula: null,
        currentParseError: null,
      };
    },
    created() {
      this.setOrCreateFormula();
      this.contextMenu.formula.id = this.vueModalID.formulaFunctions;
      this.contextMenu.fields.id = this.vueModalID.formFields;
    },
    computed: {
      formulaDynamicFieldsProps(): Hash {
        return {
          fieldsHandler: new FormulaFunctionsDynamicFieldsHandler(),
        };
      },
      hasDynamicOptions(): boolean {
        return this.dynamicOptions && this.dynamicOptions.length
      },
      formulaEvaluation(): string{
        return this.targetFormula.validateFormulaSyntax()
      }
    },
    methods: {
      editor(): CodeMirrorEditor {
        return this.$refs.formulaEditor.editor
      },
      // setup the modal
      setOrCreateFormula(): void {
        this.markToRemove = null;
        if (!this.formula) {
          this.editMode = false;
          this.targetFormula = new Formula({name: null}, 'field');
        } else {
          this.editMode = true;
          this.targetFormula = this.formula;
        }
        // use variable lookup from handler or a default one that looks through specific forms and object models
        const lookups: VariableLookupFunction[] = []
        for (const o of this.dynamicOptions) {
          if (typeof o.variableLookupFunction === 'function') {
            lookups.push(o.variableLookupFunction)
          }
        }
        let lookupFunction: VariableLookupFunction
        if (lookups.length) {
          lookupFunction = (name: string) => {
            for (const lookup of lookups) {
              const currVal = lookup(name)
              if (currVal) return currVal
            }
          }
        } else {
          lookupFunction = this.getValueFromReferenceName
        }
        this.targetFormula.lookupDynamicFieldValueBy(lookupFunction);
      },
      setupUpdateDiagnosticListener(): void {
        // need to wait 2 ticks because editor is created in nextTick callback after <formula-editor> is mounted
        Vue.nextTick(() => {
          Vue.nextTick(() => {
            this.updateEditorDiagnostics();
            this.$refs.formulaEditor.editor.codeMirror.on('change', this.updateEditorDiagnostics);
          });
        });
      },
      updateEditorDiagnostics(): void {
        const codeMirror = this.$refs.formulaEditor.editor.codeMirror;
        const doc = codeMirror.getDoc();

        codeMirror.operation(() => {
          // reset old marks
          for (const m of codeMirror.getAllMarks()) {
            if (m.isFormulaDiag) {
              m.clear();
            }
          }

          this.currentParseError = null;
          try {
            parseFormula(doc.getValue());
          } catch (e) {
            if (e instanceof ParseError) {
              let index = e.index;
              if (index === doc.getValue().length) {
                // if the error char is after the end of the text (e.g., missing closing paren in `foo(`),
                // shift it back 1 so there's still a char to highlight
                index--;
              }
              const start = doc.posFromIndex(index);
              const end = doc.posFromIndex(index + 1);
              codeMirror.markText(start, end, {
                className: "formula-diag error",
                isFormulaDiag: true,
              });
              this.currentParseError = e.message;
            } else {
              throw e;
            }
          }
        });
      },
      openPopupOnClick(e){
        if (this.hasDynamicOptions) this.openPopup(e, 'fields');
      },
      /**
       * CTA Buttons
       */
      remove($event): void {
        this.targetFormula.remove();
        this.$emit('removed', this.targetFormula);
        this.emitHideModal($event);
      },
      cancel($event): void {
        $(document).trigger('cTooltip:clear');
        this.emitHideModal($event);
        this.closePopup($event, 'fields');
        this.targetFormula.update(this.targetFormula.originalText ?? '');
        this.shouldValidate = false;
      },
      async save($event): Promise<void> {
        if (this.targetFormula.isComplete()) {
          const newValue = this.editor().codeMirror.getValue();
          await this.targetFormula.update(newValue);
          this.emitHideModal($event, this.targetFormula);
          if (!this.editMode) this.$emit("created", this.targetFormula);
          this.$emit("saved", {formulaObject: this.targetFormula});
        } else {
          this.shouldValidate = true;
        }
      },
      // Context Menus
      closePopup(event, name): void {
        this.isReplacingPlaceholder = false;
        this.hideModal(this.contextMenu[name].id);
      },
      openPopup(event, name): void {
        this.showModal(this.contextMenu[name].id);
        this.$refs[this.contextMenu[name].ref].open(event);
      },
      //Widget Management
      addWidget(type: WidgetType, $event: IDynamicDataConfig | string): void {
        switch(type) {
          case WidgetType.FormulaFunctionWidget:
            this.editor().addWidget(WidgetType.FormulaFunctionWidget, {name: $event as string}).then(widget => {
              widget.marks.forEach(mark => {
                mark.replacedWith.addEventListener('click', this.openInsertFormFieldMenuOnClick);
              });
            });
            this.closePopup($event, 'formula');
            break;
          case WidgetType.FormFieldWidget:
            this.editor().replaceWidget(WidgetType.FormFieldWidget, $event as IDynamicDataConfig).then(widget => {
              widget.marks.forEach(mark => {
                mark.replacedWith.addEventListener('click', this.openInsertFormFieldMenuOnClick);
              });
              this.markToRemove = null;
            });
            this.closePopup($event, 'fields');
            break;
          default:
            break;
        }
        this.$emit('add-widget', {type: type, config: $event})
      },
      resetMark(): void {
        this.markToRemove = null;
        this.isReplacingPlaceholder = false;
        this.closePopup(null, 'formula');
        this.closePopup(null, 'fields');
      },
      openInsertFormFieldMenuOnClick(e): void {
        if ($(e.target).hasClass('active')) {
          if (this.hasDynamicOptions) this.openPopup(e, 'fields');
        }
      },
      getDynamicFieldsTitle(options): string {
        if (!options) return "";
        if (options.title) {
          return options.title;
        } else {
          if (options.props && options.props.fieldsHandler) {
            const fieldsHandler = options.props.fieldsHandler;
            // handler can be null (salesforce fields are not implemented in formulas)
            const hasObjectModelHandler =  fieldsHandler.objectModel || fieldsHandler?.handlerConfig?.some(x => x.handler?.objectModel);
            const hasFormFieldHandler =  fieldsHandler.form || fieldsHandler?.handlerConfig?.some(x => x.handler?.form);

            if (hasFormFieldHandler && !hasObjectModelHandler) {
              return 'Form Fields';
            } else if (hasObjectModelHandler && !hasFormFieldHandler) {
              return 'Model Properties';
            } else {
              return 'Form Fields or Model Properties';
            }
          }
        }
      },
    },
    watch: {
      "state.isVisible": function(showing, hiding) {
        if (showing) {
          this.setOrCreateFormula();
          // CM editor is destroyed when the modal is closed, so we need to re-setup the listener
          this.setupUpdateDiagnosticListener();
        }
      },
    }
  });
</script>